/**
 * Police Report Tier Strategy
 *
 * Shared helper for determining what to display in the Initial Review modal
 * based on the state of police reports and the matter investigation.
 *
 * ## Tier Definitions
 *
 * Tier 1 — Confirmed report exists (most useful)
 *   Police report with attestation VERIFIED_RECEIVED or ATTESTED_VERIFIED.
 *   Show: "Yes" + report # + department name via PoliceReportLink.
 *   Case manager knows: report is confirmed, automation is retrieving it.
 *
 * Tier 2 — Report found but unconfirmed
 *   Attestation ATTESTED_UNVERIFIED, UNCLEAR, or INFERRED.
 *   Show: "Pending Verification" + department name via PoliceReportLink.
 *   Case manager knows: system found a lead, working on confirming it.
 *
 * Tier 3 — No reports, automation is running
 *   No police reports exist, investigation status is RESEARCHING or INVESTIGATING.
 *   Show: "Searching".
 *   Case manager knows: no action needed, system is working.
 *
 * Tier 4 — Automation is stuck
 *   Investigation status is ESCALATED.
 *   Show: "Needs Attention" + description.
 *   Case manager knows: something needs manual intervention.
 *
 * Tier 5 — Missing essential data
 *   Investigation status is NEED_INFO.
 *   Show: "Missing Info" + description.
 *   Case manager knows: they need to fill in fields before automation can start.
 *
 * Tier 6 — Not applicable
 *   Investigation NOT_APPLICABLE, CLOSED_NO_REPORT, or BACKFILLED_CLOSED.
 *   Show: "N/A".
 *
 * Tier 0 — Fallback (no data at all)
 *   Show: em-dash / "Unknown".
 *
 * ## Data Sources
 *
 * 1. GET /api/matters/{matterId}/police-reports/   → Covers Tiers 1–2
 * 2. GET /api/matters/{matterId}/police-investigation/ → Covers Tiers 3–6
 */
import type { DetailedPoliceReport } from '@/services/api/generated/policeReportsService';

import type { ReadMatterInvestigation } from '@/services/api/generated/policeInvestigationService';

// ============================================================================
// Constants
// ============================================================================

/** Attestation priority map — higher value = more confirmed */
const ATTESTATION_PRIORITY: Record<string, number> = {
  verified_received: 5,
  attested_verified: 4,
  attested_unverified: 3,
  unclear: 2,
  inferred: 1,
  rejected_verified: 0,
};

/** Attestation statuses that indicate a confirmed report (Tier 1) */
const CONFIRMED_STATUSES = new Set(['verified_received', 'attested_verified']);

/** Attestation statuses that indicate a found-but-unconfirmed report (Tier 2) */
const UNCONFIRMED_STATUSES = new Set([
  'attested_unverified',
  'inferred',
  'unclear',
]);

// ============================================================================
// Types
// ============================================================================

type PoliceReportTierBase = {
  /** Unique key for call-prep state tracking */
  sourceId: string;
  /** Pre-computed text to add to call prep notes */
  callPrepText: string;
};

export type PoliceReportTierResult =
  | (PoliceReportTierBase & {
      tier: 1;
      label: 'Yes';
      report?: DetailedPoliceReport;
    })
  | (PoliceReportTierBase & {
      tier: 2;
      label: 'Pending Verification';
      report: DetailedPoliceReport;
    })
  | (PoliceReportTierBase & {
      tier: 3;
      label: 'Searching';
      description?: string | null;
    })
  | (PoliceReportTierBase & {
      tier: 4;
      label: 'Needs Attention';
      description?: string | null;
    })
  | (PoliceReportTierBase & {
      tier: 5;
      label: 'Missing Info';
      description?: string | null;
    })
  | (PoliceReportTierBase & { tier: 6; label: 'N/A' })
  | (PoliceReportTierBase & { tier: 0; label: 'Unknown' });

// ============================================================================
// Helper
// ============================================================================

/**
 * Select the highest-priority police report by attestation status.
 * Returns null when the array is empty.
 */
function selectBestReport(
  reports: DetailedPoliceReport[],
): DetailedPoliceReport | null {
  if (reports.length === 0) return null;

  return reports.reduce((best, current) => {
    const bestPriority =
      ATTESTATION_PRIORITY[best.attestation_status || ''] ?? 1;
    const currentPriority =
      ATTESTATION_PRIORITY[current.attestation_status || ''] ?? 1;
    return currentPriority > bestPriority ? current : best;
  }, reports[0]);
}

// ============================================================================
// Main Function
// ============================================================================

/**
 * Determine the police report tier for a matter.
 *
 * @param policeReports - All police reports for the matter (from police-reports API)
 * @param investigation - The matter investigation record (from police-investigation API)
 * @returns A discriminated union describing the tier, label, and call-prep metadata
 */
export function getPoliceReportTier(
  policeReports: DetailedPoliceReport[],
  investigation: ReadMatterInvestigation | null | undefined,
): PoliceReportTierResult {
  const bestReport = selectBestReport(policeReports);
  const attestation = bestReport?.attestation_status || '';

  // ------------------------------------------------------------------
  // Tier 1 — Confirmed report (attestation verified or received)
  // ------------------------------------------------------------------
  if (bestReport && CONFIRMED_STATUSES.has(attestation)) {
    const deptName = bestReport.police_department?.name || 'Department';
    const reportNum = bestReport.report_identifier;
    return {
      tier: 1,
      label: 'Yes',
      report: bestReport,
      sourceId: 'police_report',
      callPrepText: reportNum
        ? `Police report confirmed: ${deptName}, Report #${reportNum}`
        : `Police report confirmed: ${deptName}`,
    };
  }

  // ------------------------------------------------------------------
  // Tier 2 — Report found but unconfirmed
  // ------------------------------------------------------------------
  if (bestReport && UNCONFIRMED_STATUSES.has(attestation)) {
    const deptName = bestReport.police_department?.name || 'Department';
    return {
      tier: 2,
      label: 'Pending Verification',
      report: bestReport,
      sourceId: 'police_report',
      callPrepText: `Police report pending verification: ${deptName}`,
    };
  }

  // ------------------------------------------------------------------
  // Tiers 3–6 — Fall through to investigation status
  // ------------------------------------------------------------------
  const status = investigation?.status;

  if (status === 'researching' || status === 'investigating') {
    return {
      tier: 3,
      label: 'Searching',
      description: investigation?.description,
      sourceId: 'police_report',
      callPrepText: 'Police report search in progress',
    };
  }

  if (status === 'escalated') {
    return {
      tier: 4,
      label: 'Needs Attention',
      description: investigation?.description,
      sourceId: 'police_report',
      callPrepText: 'Police report needs attention — follow up required',
    };
  }

  if (status === 'need_info') {
    return {
      tier: 5,
      label: 'Missing Info',
      description: investigation?.description,
      sourceId: 'police_report',
      callPrepText: 'Police report missing info — verify incident details',
    };
  }

  if (
    status === 'not_applicable' ||
    status === 'closed_no_report' ||
    status === 'backfilled_closed'
  ) {
    return {
      tier: 6,
      label: 'N/A',
      sourceId: 'police_report',
      callPrepText: 'Police report: N/A',
    };
  }

  // Investigation says complete but we already checked reports above —
  // if we get here with completed_report_received, show Tier 1 with
  // whatever best report we have (may be rejected or null).
  if (status === 'completed_report_received') {
    return {
      tier: 1,
      label: 'Yes',
      report: bestReport ?? undefined,
      sourceId: 'police_report',
      callPrepText: bestReport?.police_department?.name
        ? `Police report confirmed: ${bestReport.police_department.name}`
        : 'Police report confirmed',
    };
  }

  // ------------------------------------------------------------------
  // Tier 0 — Fallback
  // ------------------------------------------------------------------
  return {
    tier: 0,
    label: 'Unknown',
    sourceId: 'police_report',
    callPrepText: 'Follow up on police report status',
  };
}
